// src/components/Projects.js

import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaExternalLinkAlt } from "react-icons/fa";
import ProjectCard from "./ProjectCard";
import TimelineView from "./TimelineView";
import TimelineToggle from "./TimelineToggle";
import { useTimelineView } from "../hooks/useTimelineView";
import { useData } from "../hooks/useData";
import { getProjects } from "../firebase/dataService";
import "./Projects.css";

const SkeletonCard = () => (
  <div className="project-skeleton">
    <div className="skeleton" style={{ height: "180px" }} />
    <div style={{ padding: "1.25rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      <div className="skeleton" style={{ height: "14px", width: "40%" }} />
      <div className="skeleton" style={{ height: "18px", width: "80%" }} />
      <div className="skeleton" style={{ height: "12px" }} />
      <div className="skeleton" style={{ height: "12px", width: "70%" }} />
    </div>
  </div>
);

const ProjectTimelineCard = ({ title, text, stack, view, date }) => {
  const preview = (text || "").length > 100 ? text.slice(0, 100).trimEnd() + "…" : text;
  const tags = (stack || "").split(",").map(s => s.trim()).filter(Boolean).slice(0, 4);
  return (
    <>
      <div className="tl-view__card__title">{title}</div>
      {preview && <div className="tl-view__card__meta">{preview}</div>}
      {tags.length > 0 && (
        <div className="tl-view__card__tags">
          {tags.map(t => <span key={t} className="tl-view__card__tag">{t}</span>)}
        </div>
      )}
      {view && view !== "#" && (
        <a href={view} target="_blank" rel="noreferrer" className="tl-view__card__link">
          <FaExternalLinkAlt size={10} /> View project
        </a>
      )}
    </>
  );
};

const Projects = ({ limit, title = "Projects", showAll = false }) => {
  const { data: projects, loading, error } = useData(getProjects);
  const [view, setView] = useTimelineView("projects", "grid");

  const displayed = limit ? projects.slice(0, limit) : projects;

  return (
    <section className="projects-section">
      <div className="container">
        <div className="projects-section__header">
          <div>
            <p className="section-label">Work</p>
            <h2 className="section-title">{title}</h2>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
            <TimelineToggle view={view} onChange={setView} />
            {showAll && (
              <Link to="/projects" className="btn btn-outline">
                All Projects <FaArrowRight size={13} />
              </Link>
            )}
          </div>
        </div>

        {error && (
          <p style={{ color: "var(--accent)", textAlign: "center", padding: "2rem" }}>
            Failed to load projects. Please try again.
          </p>
        )}

        {loading ? (
          <div className="projects-grid">
            {Array.from({ length: limit || 3 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : view === "timeline" ? (
          <TimelineView
            items={displayed}
            getDate={p => p.date || ""}
            accentColor="#e86c2f"
            renderCard={p => <ProjectTimelineCard {...p} />}
          />
        ) : (
          <div className="projects-grid">
            {displayed.map(project => <ProjectCard key={project.id} {...project} />)}
          </div>
        )}
      </div>
    </section>
  );
};

export default Projects;